import React, { useEffect, useMemo, useState } from "react";

const ITEMS_PER_PAGE = 10;
const MAX_VISIBLE_PAGES = 3;

function scrollToMain() {
  if (window.jQuery) {
    const main = window.jQuery(".main");

    if (main.length) {
      window.jQuery("html, body").animate({ scrollTop: main.offset().top }, 300);
      return;
    }
  }

  const main = document.querySelector(".main");

  if (main) {
    main.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function readInitialState(orderByOptions) {
  const searchParams = new URLSearchParams(window.location.search);
  const requestedOrderBy = decodeURIComponent(searchParams.get("orderBy") || "");
  const requestedPage = Number.parseInt(searchParams.get("currentPage") || "", 10);
  const initialOrderBy = orderByOptions.includes(requestedOrderBy) ? requestedOrderBy : orderByOptions[0];

  return {
    orderBy: initialOrderBy,
    currentPage: Number.isFinite(requestedPage) && requestedPage > 0 ? requestedPage : 1,
  };
}

function buildPageUrl(baseUrl, orderBy, currentPage, allLabel) {
  const nextUrl = new URL(baseUrl, window.location.origin);

  nextUrl.searchParams.set("orderBy", orderBy || allLabel);
  nextUrl.searchParams.set("currentPage", String(currentPage));

  return `${nextUrl.pathname}${nextUrl.search}`;
}

function buildPageItems(currentPage, totalPages) {
  if (totalPages <= MAX_VISIBLE_PAGES) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = [1];
  const startPage = Math.max(2, currentPage - 1);
  const endPage = Math.min(totalPages - 1, currentPage + 1);

  if (startPage > 2) {
    pages.push("...");
  }

  for (let page = startPage; page <= endPage; page += 1) {
    pages.push(page);
  }

  if (endPage < totalPages - 1) {
    pages.push("...");
  }

  pages.push(totalPages);

  return pages;
}

function Pagination({ currentPage, totalItems, pageSize, onPageChange, previousLabel, nextLabel }) {
  const totalPages = Math.ceil(totalItems / pageSize);

  if (totalPages <= 1) {
    return null;
  }

  const pageItems = buildPageItems(currentPage, totalPages);

  return (
    <ul role="menu" className="pagination">
      <li role="menuitem" className={`pagination-prev ${currentPage === 1 ? "disabled" : ""}`}>
        <a
          href="#"
          aria-label={previousLabel}
          aria-disabled={currentPage === 1}
          tabIndex={currentPage === 1 ? -1 : 0}
          onClick={(event) => {
            event.preventDefault();
            if (currentPage > 1) {
              onPageChange(currentPage - 1);
            }
          }}
        >
          {previousLabel}
        </a>
      </li>
      {pageItems.map((pageItem, index) =>
        pageItem === "..." ? (
          <li key={`ellipsis-${index}`} role="menuitem" className="pagination-page" aria-hidden="true">
            <a href="#" onClick={(event) => event.preventDefault()} tabIndex={-1}>
              ...
            </a>
          </li>
        ) : (
          <li key={pageItem} role="menuitem" className={`pagination-page ${pageItem === currentPage ? "active" : ""}`}>
            <a
              href="#"
              aria-current={pageItem === currentPage ? "page" : undefined}
              onClick={(event) => {
                event.preventDefault();
                onPageChange(pageItem);
              }}
            >
              {pageItem}
            </a>
          </li>
        ),
      )}
      <li role="menuitem" className={`pagination-next ${currentPage === totalPages ? "disabled" : ""}`}>
        <a
          href="#"
          aria-label={nextLabel}
          aria-disabled={currentPage === totalPages}
          tabIndex={currentPage === totalPages ? -1 : 0}
          onClick={(event) => {
            event.preventDefault();
            if (currentPage < totalPages) {
              onPageChange(currentPage + 1);
            }
          }}
        >
          {nextLabel}
        </a>
      </li>
    </ul>
  );
}

function FilterDropdown({ options, orderBy, onSelect, buttonId }) {
  return (
    <div className="dropdown">
      <button
        className="btn-dropdown dropdown-toggle pr-0"
        type="button"
        id={buttonId}
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {orderBy}
      </button>
      <div className="dropdown-menu" aria-labelledby={buttonId}>
        {options.map((item) => (
          <button
            key={item}
            className={`dropdown-item ${item === orderBy ? "active" : ""}`}
            type="button"
            onClick={() => onSelect(item)}
          >
            {item}
          </button>
        ))}
      </div>
    </div>
  );
}

function EventCard({ item }) {
  return (
    <div className="mb-5 card-news blueBarBottom">
      <a href={item.url} className="link d-flex flex-column h-100">
        <div className="title h3 p-3">{item.title}</div>
        <div className="date mt-auto px-3 pb-3">{item.dateFormatted}</div>
      </a>
    </div>
  );
}

export default function EventListIsland({ config }) {
  const orderByOptions = useMemo(
    () => (Array.isArray(config.orderByOptions) && config.orderByOptions.length > 0 ? config.orderByOptions : ["All"]),
    [config.orderByOptions],
  );
  const initialState = useMemo(() => readInitialState(orderByOptions), [orderByOptions]);

  const [orderBy, setOrderBy] = useState(initialState.orderBy);
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const [eventsData, setEventsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasRedirectedToBase, setHasRedirectedToBase] = useState(false);

  useEffect(() => {
    let isActive = true;

    async function loadEvents() {
      document.documentElement.classList.add("loading");
      setLoading(true);

      try {
        const response = await fetch(config.eventsApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify({
            nodeId: String(config.nodeId),
            orderBy: orderBy !== config.allLabel ? orderBy : "",
          }),
        });

        if (!response.ok) {
          throw new Error(`Event request failed with ${response.status}`);
        }

        const result = await response.json();

        if (!isActive) {
          return;
        }

        const nextItems = Array.isArray(result) ? result : [];

        if (!hasRedirectedToBase && nextItems.length === 0 && orderBy !== config.allLabel) {
          const resetUrl = new URL(config.baseUrl, window.location.origin);
          window.history.pushState({ path: resetUrl.pathname }, document.title, `${resetUrl.pathname}${resetUrl.search}`);
          setHasRedirectedToBase(true);
          setOrderBy(config.allLabel);
          setCurrentPage(1);
          return;
        }

        setEventsData(nextItems);

        const totalPages = Math.max(1, Math.ceil(nextItems.length / ITEMS_PER_PAGE));
        const nextPage = Math.min(currentPage, totalPages);

        if (nextPage !== currentPage) {
          setCurrentPage(nextPage);
          return;
        }

        const nextUrl = buildPageUrl(config.baseUrl, orderBy, nextPage, config.allLabel);
        window.history.pushState({ path: nextUrl }, document.title, nextUrl);
        scrollToMain();
      } catch (error) {
        if (!isActive) {
          return;
        }

        setEventsData([]);
      } finally {
        if (!isActive) {
          return;
        }

        setLoading(false);
        document.documentElement.classList.remove("loading");
      }
    }

    loadEvents();

    return () => {
      isActive = false;
      document.documentElement.classList.remove("loading");
    };
  }, [config.allLabel, config.baseUrl, config.eventsApiUrl, config.nodeId, currentPage, hasRedirectedToBase, orderBy]);

  const paginatedData = useMemo(() => {
    if (!eventsData.length) {
      return [];
    }

    const firstIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const stopBeforeIndex = Math.min(currentPage * ITEMS_PER_PAGE, eventsData.length);

    return eventsData.slice(firstIndex, stopBeforeIndex);
  }, [currentPage, eventsData]);

  return (
    <>
      <div className="container mt-3">
        <div className="row">
          <div className="col-md-6 mb-3">
            <div className="d-flex flex-wrap align-items-center h-100">
              <a href={config.gridUrl} className="mr-2" style={{ width: "25px", height: "25px", opacity: 0.5 }}>
                <img src="/assets/images/grid-view.svg" className="grid-view-icon" alt="Grid view icon" title="Grid view" />
              </a>
              <img
                src="/assets/images/list-view.svg"
                style={{ maxWidth: "25px", height: "auto" }}
                className="list-view-icon mr-2"
                alt="List view icon"
                title="List view"
              />
              <span className="viewText">List view</span>
            </div>
          </div>
          <div className="col-md-6 col-xl-4 offset-xl-2 mb-3 d-flex flex-column flex-sm-row align-items-sm-center">
            <span className="my-0 mr-3 flex-shrink-0">{config.filterByLabel}</span>
            <FilterDropdown options={orderByOptions} orderBy={orderBy} onSelect={(item) => {
              setOrderBy(item);
              setCurrentPage(1);
              setHasRedirectedToBase(false);
            }} buttonId="orderByDropdown" />
          </div>
        </div>
      </div>

      {!loading && paginatedData.length > 0 ? (
        <div className="container my-5">
          <div className="row">
            <div className="col-lg-9">
              <div id="newsCards">
                {paginatedData.map((item) => (
                  <EventCard key={item.nodeId} item={item} />
                ))}
              </div>
              <Pagination
                currentPage={currentPage}
                totalItems={eventsData.length}
                pageSize={ITEMS_PER_PAGE}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  scrollToMain();
                }}
                previousLabel={config.previousLabel}
                nextLabel={config.nextLabel}
              />
            </div>
          </div>
        </div>
      ) : null}

      {!loading && paginatedData.length === 0 ? (
        <div className="container my-5">
          <h3 className="sorry">{config.emptyStateText}</h3>
        </div>
      ) : null}
    </>
  );
}
